import { config } from "./config";
import { createSseParser } from "./sse";
import type { AgentOutput, OverageReport } from "./report";

const API_BASE = `${config.apiBaseUrl}/v2`;
export const REPORT_TOOL = "compute_overage_report";
export const DEFAULT_AGENT_KEY = config.defaultAgentKey;
export const DEFAULT_TOKEN_ENDPOINT = config.oauthTokenEndpoint;

export interface AdminCredentials {
  clientId: string;
  clientSecret: string;
  tokenEndpoint: string;
}

export interface AgentEvent {
  id?: string;
  type: string;
  created_at?: string;
  tool_configuration_name?: string;
  tool_input?: Record<string, unknown>;
  tool_output?: Record<string, unknown>;
  error?: boolean;
  messages?: string[];
  content?: unknown;
  skill_name?: string;
  artifacts?: { filename?: string; artifact_id?: string }[];
}

export class VectaraApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function errorMessage(response: Response): Promise<string> {
  const text = await response.text();
  try {
    const body = JSON.parse(text) as { messages?: string[]; field_errors?: Record<string, string>; message?: string };
    const parts = [
      ...(body.messages ?? []),
      ...Object.entries(body.field_errors ?? {}).map(([field, error]) => `${field}: ${error}`)
    ];
    if (body.message) parts.push(body.message);
    if (parts.length > 0) return parts.join("; ");
  } catch {
    // fall through to the raw body
  }
  return text || response.statusText;
}

async function call<T>(apiKey: string, method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "x-api-key": apiKey, ...(body === undefined ? {} : { "content-type": "application/json" }) },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!response.ok) {
    throw new VectaraApiError(
      `${method} ${path} failed (${response.status}): ${await errorMessage(response)}`,
      response.status
    );
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export async function verifyAgent(apiKey: string, agentKey: string): Promise<string> {
  const agent = await call<{ name: string }>(apiKey, "GET", `/agents/${encodeURIComponent(agentKey)}`);
  return agent.name;
}

export async function saveAgentCredentials(
  apiKey: string,
  agentKey: string,
  credentials: AdminCredentials
): Promise<void> {
  const key = encodeURIComponent(agentKey);
  await call(apiKey, "PATCH", `/agents/${key}/secrets`, {
    secrets: { admin_client_id: credentials.clientId, admin_client_secret: credentials.clientSecret }
  });
  const agent = await call<{ metadata?: Record<string, unknown> }>(apiKey, "GET", `/agents/${key}`);
  await call(apiKey, "PATCH", `/agents/${key}`, {
    metadata: { ...(agent.metadata ?? {}), oauth_token_endpoint: credentials.tokenEndpoint }
  });
}

export async function createSession(
  apiKey: string,
  agentKey: string,
  name: string,
  credentials: AdminCredentials | null
): Promise<string> {
  const body = credentials
    ? {
        name,
        secrets: { admin_client_id: credentials.clientId, admin_client_secret: credentials.clientSecret },
        metadata: { oauth_token_endpoint: credentials.tokenEndpoint }
      }
    : { name };
  const session = await call<{ key: string }>(apiKey, "POST", `/agents/${encodeURIComponent(agentKey)}/sessions`, body);
  return session.key;
}

export async function streamInput(
  apiKey: string,
  agentKey: string,
  sessionKey: string,
  message: string,
  files: File[],
  onEvent: (event: AgentEvent) => void,
  signal: AbortSignal
): Promise<void> {
  const form = new FormData();
  form.append(
    "messages",
    new Blob([JSON.stringify([{ type: "text", content: message }])], { type: "application/json" })
  );
  form.append("stream_response", "true");
  files.forEach((file) => form.append("files", file, file.name));
  const response = await fetch(
    `${API_BASE}/agents/${encodeURIComponent(agentKey)}/sessions/${encodeURIComponent(sessionKey)}/events`,
    {
      method: "POST",
      headers: { "x-api-key": apiKey, accept: "text/event-stream" },
      body: form,
      signal
    }
  );
  if (!response.ok || !response.body) {
    throw new VectaraApiError(
      `Sending the request to the agent failed (${response.status}): ${await errorMessage(response)}`,
      response.status
    );
  }
  const parser = createSseParser();
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    parser.push(value).forEach((event) => onEvent(event as AgentEvent));
  }
  parser.flush().forEach((event) => onEvent(event as AgentEvent));
}

export async function listEvents(apiKey: string, agentKey: string, sessionKey: string): Promise<AgentEvent[]> {
  const events: AgentEvent[] = [];
  let pageKey: string | undefined;
  do {
    const query = new URLSearchParams({ limit: "100", ...(pageKey ? { page_key: pageKey } : {}) });
    const page = await call<{ events: AgentEvent[]; metadata?: { page_key?: string } }>(
      apiKey,
      "GET",
      `/agents/${encodeURIComponent(agentKey)}/sessions/${encodeURIComponent(sessionKey)}/events?${query}`
    );
    events.push(...page.events);
    pageKey = page.metadata?.page_key;
  } while (pageKey);
  return events;
}

export interface SessionOutcome {
  agentOutput: AgentOutput | null;
  report: OverageReport | null;
  verificationIssues: string[];
  errors: string[];
}

const VERIFICATION_HEADER = "Fix each issue and call again:";

export function verificationIssues(toolOutput: Record<string, unknown> | undefined): string[] {
  const text = typeof toolOutput?.error === "string" ? toolOutput.error : "";
  const start = text.indexOf(VERIFICATION_HEADER);
  if (start < 0) return [];
  const issues: string[] = [];
  for (const line of text.slice(start + VERIFICATION_HEADER.length).split("\n")) {
    if (line.startsWith("- ")) issues.push(line.slice(2));
    else if (issues.length > 0 && line.trim() !== "") break;
  }
  return issues;
}

export function sessionOutcome(events: AgentEvent[]): SessionOutcome {
  const chronological = [...events].sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""));
  const turnStart = chronological.map((event) => event.type).lastIndexOf("input_message");
  const turn = turnStart < 0 ? chronological : chronological.slice(turnStart);
  const isReportOutput = (event: AgentEvent) =>
    event.type === "tool_output" && event.tool_configuration_name === REPORT_TOOL;
  const structured = turn.filter((event) => event.type === "structured_output");
  const reports = chronological.filter((event) => isReportOutput(event) && event.error !== true);
  return {
    agentOutput: structured.length > 0 ? (structured[structured.length - 1].content as AgentOutput) : null,
    report: reports.length > 0 ? (reports[reports.length - 1].tool_output as unknown as OverageReport) : null,
    verificationIssues: turn
      .filter((event) => isReportOutput(event) && event.error === true)
      .flatMap((event) => verificationIssues(event.tool_output)),
    errors: turn
      .filter((event) => event.type === "error")
      .map((event) => (event.messages ?? []).join("; ") || "The agent stopped with an unexpected error.")
  };
}

export interface AgentMemory {
  content: string;
  version: number;
  lastUpdated: string | null;
  lastUpdatedBy: string | null;
}

interface MemoryResponse {
  skill: { content: string };
  metadata: { memory_version: number; memory_last_updated?: string | null; memory_last_updated_by?: string | null };
}

function toMemory(response: MemoryResponse): AgentMemory {
  return {
    content: response.skill.content,
    version: response.metadata.memory_version,
    lastUpdated: response.metadata.memory_last_updated ?? null,
    lastUpdatedBy: response.metadata.memory_last_updated_by ?? null
  };
}

export async function getMemory(apiKey: string, agentKey: string): Promise<AgentMemory> {
  return toMemory(await call<MemoryResponse>(apiKey, "GET", `/agents/${encodeURIComponent(agentKey)}/memory`));
}

export async function updateMemory(
  apiKey: string,
  agentKey: string,
  content: string,
  expectedVersion: number
): Promise<AgentMemory> {
  return toMemory(
    await call<MemoryResponse>(apiKey, "PATCH", `/agents/${encodeURIComponent(agentKey)}/memory`, {
      content,
      updated_by: "overage-reporter-ui",
      expected_version: expectedVersion
    })
  );
}
